import { useEffect, useState } from 'react';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import { Link } from 'react-router-dom';
import { useTier } from '../hooks/useTier.js';
import { useRoleLabel } from '../hooks/useRoleLabel.js';
import { useAuth } from '../contexts/AuthContext.js';
import Layout from '../components/Layout.js';
import { UpgradePrompt } from '../components/UpgradePrompt.js';
import { ArrowRight, Check, ChevronRight, CreditCard, HelpCircle, Link2, Settings, UserPlus, Wallet } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { TIER_PRICING, TRIAL_DAYS } from '@vcard/shared';

interface RecentUser {
  id: string;
  email: string;
  role: string;
  created_at: string;
}

interface Stats {
  userCount: number;
  cardCount: number;
  departmentCount: number;
  organisationName: string;
}

interface DailyPoint { day: string; views: number; unique: number }
interface ViewStats { total: number; visitors: number; daily: DailyPoint[] }

export default function Dashboard() {
  const { t } = useAppTranslation();
  const { user } = useAuth();
  const { tier, baseTier, override, locked, seatTier, trialDaysRemaining, limits, usage, loading: tierLoading, hasFeature } = useTier();
  const [stats, setStats] = useState<Stats | null>(null);
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [views, setViews] = useState<ViewStats | null>(null);

  const isAdmin = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'superadmin';
  const canSeeAnalytics = !tierLoading && isAdmin && hasFeature('analytics');

  useEffect(() => {
    Promise.all([
      fetch('/api/admin/users', { credentials: 'include' })
        .then((r) => r.ok ? r.json() : []),
      fetch('/api/admin/departments', { credentials: 'include' })
        .then((r) => r.ok ? r.json() : { departments: [] }),
    ])
      .then(([usersData, deptData]) => {
        const users: RecentUser[] = Array.isArray(usersData)
          ? usersData as RecentUser[]
          : ((usersData as { users?: RecentUser[] }).users ?? []);

        const depts = Array.isArray(deptData?.departments)
          ? deptData.departments as unknown[]
          : [];

        // Sum card counts across all users
        const cardCount = (Array.isArray(usersData) ? usersData : []).reduce(
          (acc: number, u: { cards?: unknown[] }) => acc + (u.cards?.length ?? 0),
          0
        );

        const orgName =
          (Array.isArray(usersData) && usersData.length > 0
            ? (usersData[0] as { organisationName?: string }).organisationName
            : undefined) ?? '';

        setStats({
          userCount: users.length,
          cardCount,
          departmentCount: depts.length,
          organisationName: orgName,
        });

        // Sort by created_at desc (already sorted by API) and take first 5
        setRecentUsers(users.slice(0, 5));
      })
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, []);

  // Card views of the last 7 days (a Business feature)
  useEffect(() => {
    if (!canSeeAnalytics) return;
    fetch('/api/analytics/organisation?range=7d', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { stats?: { summary?: { totalViews?: number; uniqueSessions?: number }; daily?: DailyPoint[] } } | null) => {
        if (!d?.stats) return;
        setViews({
          total: d.stats.summary?.totalViews ?? 0,
          visitors: d.stats.summary?.uniqueSessions ?? 0,
          daily: d.stats.daily ?? [],
        });
      })
      .catch(() => undefined);
  }, [canSeeAnalytics]);

  const inTrial = baseTier === 'free' && !override && !locked && trialDaysRemaining !== null;
  const hasPlan = baseTier !== 'free' || Boolean(override);
  const planLabel = TIER_PRICING[seatTier ?? tier].label;

  // Setup steps with a state we can really know
  const canInvite = limits.users === null || limits.users > 1;
  const steps: Step[] = [
    {
      id: 'card',
      done: (stats?.cardCount ?? 0) > 0,
      title: t('dashboard.step.card', 'Erste Karte erstellen'),
      text: t('dashboard.step.cardText', 'Name, Foto und Kontaktdaten eintragen. Danach ist sie unter ihrem Link und QR-Code erreichbar.'),
      to: '/cards/new',
      cta: t('dashboard.step.cardCta', 'Karte erstellen'),
    },
    ...(canInvite
      ? [{
          id: 'team',
          done: (stats?.userCount ?? 0) > 1,
          title: t('dashboard.step.team', 'Team einladen'),
          text: t('dashboard.step.teamText', 'Jeder Nutzer bekommt seine eigene Karte im einheitlichen Design.'),
          to: '/users',
          cta: t('dashboard.inviteUsers', 'Nutzer einladen'),
        }]
      : []),
    {
      id: 'plan',
      done: hasPlan,
      title: t('dashboard.step.plan', 'Plan wählen'),
      text: t('dashboard.step.planText', 'Mit einem Plan bleiben deine Karten auch nach dem Testmonat online.'),
      to: '/billing',
      cta: t('dashboard.choosePlan', 'Plan wählen'),
    },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const nextStep = steps.find((s) => !s.done);

  return (
    <Layout title={stats?.organisationName ? `${stats.organisationName} · Dashboard` : 'Dashboard'}>
      <div className="dash-in max-w-6xl space-y-6">

        {/* Plan status */}
        {locked ? (
          <section role="alert" className="flex flex-col gap-3 rounded-2xl border border-danger-500/30 bg-danger-100 dark:bg-danger-500/10 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm font-medium text-danger-700 dark:text-danger-300">{t('dashboard.trialExpired')}</p>
            <Link to="/billing" className="inline-flex items-center justify-center gap-1.5 rounded-full bg-danger-600 px-4 py-2 text-sm font-semibold text-white hover:bg-danger-500 transition-colors whitespace-nowrap">
              {t('dashboard.choosePlan', 'Plan wählen')} <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </section>
        ) : (
          <section className="rounded-2xl border border-line bg-panel px-5 py-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 flex-1">
                {inTrial ? (
                  <>
                    <p className="text-base font-semibold text-ink">
                      {t('dashboard.trialDays', 'Testmonat: noch {{days}} Tage', { days: trialDaysRemaining })}
                    </p>
                    <p className="mt-0.5 text-sm text-ink-muted">
                      {t('dashboard.trialNote', 'Danach zeigen deine Karten „Unbezahlte Version“, bis du einen Plan buchst.')}
                    </p>
                    {(trialDaysRemaining ?? 0) <= TRIAL_DAYS && (
                      <div
                        className="mt-3 h-1.5 w-full max-w-md overflow-hidden rounded-full bg-raised"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={TRIAL_DAYS}
                        aria-valuenow={TRIAL_DAYS - (trialDaysRemaining ?? 0)}
                        aria-label={t('dashboard.trialProgress', 'Fortschritt des Testmonats')}
                      >
                        <div
                          className="h-full rounded-full bg-accent-500 transition-[width] duration-700 ease-out"
                          style={{ width: `${Math.min(100, Math.max(0, ((TRIAL_DAYS - (trialDaysRemaining ?? 0)) / TRIAL_DAYS) * 100))}%` }}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-base font-semibold text-ink">{planLabel}</p>
                    {limits.users !== null && !tierLoading && (
                      <>
                        <p className="mt-0.5 text-sm text-ink-muted">
                          {t('dashboard.seatsUsed', '{{used}} von {{total}} Plätzen belegt', { used: usage.users, total: limits.users })}
                        </p>
                        <div className="mt-3 h-1.5 w-full max-w-md overflow-hidden rounded-full bg-raised">
                          <div
                            className="h-full rounded-full bg-accent-500 transition-[width] duration-700 ease-out"
                            style={{ width: `${Math.min(100, limits.users ? (usage.users / limits.users) * 100 : 0)}%` }}
                          />
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
              <Link
                to="/billing"
                className={`inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${
                  inTrial ? 'bg-accent-600 text-white hover:bg-accent-500' : 'border border-line text-ink-muted hover:text-ink hover:border-line-strong'
                }`}
              >
                {inTrial ? t('dashboard.choosePlan', 'Plan wählen') : t('dashboard.managePlan', 'Plan verwalten')}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </section>
        )}

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-6">

            {/* Setup */}
            {doneCount < steps.length && !statsLoading && (
              <section className="rounded-2xl border border-line bg-panel">
                <div className="flex items-baseline justify-between gap-3 px-5 pt-5">
                  <h2 className="text-base font-semibold text-ink">{t('dashboard.start', 'Erste Schritte')}</h2>
                  <p className="text-xs text-ink-faint tabular-nums">
                    {t('dashboard.startProgress', '{{done}} von {{total}} erledigt', { done: doneCount, total: steps.length })}
                  </p>
                </div>
                <ul className="mt-3 divide-y divide-line">
                  {steps.map((s) => (
                    <li key={s.id} className="flex items-center gap-4 px-5 py-4">
                      <span
                        aria-hidden="true"
                        className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border ${
                          s.done ? 'border-success-500 bg-success-500 text-white' : 'border-line-strong text-transparent'
                        }`}
                      >
                        <Check className="h-4 w-4" strokeWidth={2.5} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium ${s.done ? 'text-ink-faint line-through' : 'text-ink'}`}>{s.title}</p>
                        {!s.done && <p className="mt-0.5 text-sm text-ink-muted">{s.text}</p>}
                      </div>
                      {!s.done && (
                        <Link
                          to={s.to}
                          className={`flex-shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                            s.id === nextStep?.id
                              ? 'bg-accent-600 text-white hover:bg-accent-500'
                              : 'border border-line text-ink-muted hover:text-ink hover:border-line-strong'
                          }`}
                        >
                          {s.cta}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Views of the last 7 days */}
            {canSeeAnalytics && views && <ViewsPanel views={views} />}
            {!tierLoading && isAdmin && !hasFeature('analytics') && (
              <UpgradePrompt
                requiredTier="business"
                message={t('dashboard.analyticsLocked', 'Mit Analytics siehst du, wie oft deine Karten geöffnet werden und woher die Besucher kommen.')}
              />
            )}

            {/* Team */}
            {isAdmin && (
              <section className="rounded-2xl border border-line bg-panel p-5">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-semibold text-ink">{t('dashboard.team', 'Team')}</h2>
                  <Link to="/users" className="inline-flex items-center gap-1 text-sm text-accent-700 hover:text-accent-600 dark:text-accent-300 dark:hover:text-accent-200 transition-colors">
                    {t('dashboard.showAll')} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
                {statsLoading ? (
                  <div className="mt-4 space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-10 animate-pulse rounded-lg bg-raised" />
                    ))}
                  </div>
                ) : recentUsers.length === 0 ? (
                  <p className="py-6 text-center text-sm text-ink-faint">{t('dashboard.noUsers')}</p>
                ) : (
                  <ul className="mt-2 divide-y divide-line">
                    {recentUsers.map((u) => (
                      <li key={u.id} className="flex items-center justify-between gap-3 py-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <span aria-hidden="true" className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-raised text-xs font-semibold text-ink-muted">
                            {u.email.slice(0, 1).toUpperCase()}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-ink">{u.email}</p>
                            <p className="text-xs text-ink-faint">{formatDate(u.created_at)}</p>
                          </div>
                        </div>
                        <RoleBadge role={u.role} />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
          </div>

          <aside className="space-y-6">
            {/* Numbers */}
            <section className="rounded-2xl border border-line bg-panel p-2">
              <h2 className="px-3 pb-1 pt-3 text-base font-semibold text-ink">{t('dashboard.overview', 'Überblick')}</h2>
              <NumberRow
                to="/users"
                label={t('dashboard.users')}
                value={statsLoading ? '…' : String(stats?.userCount ?? 0)}
                of={limits.users !== null && !tierLoading ? `/ ${limits.users}` : ''}
              />
              <NumberRow to="/cards" label={t('cards.title')} value={statsLoading ? '…' : String(stats?.cardCount ?? 0)} of="" />
              <NumberRow to="/departments" label={t('dashboard.departments')} value={statsLoading ? '…' : String(stats?.departmentCount ?? 0)} of="" />
            </section>

            {/* Shortcuts */}
            <section className="rounded-2xl border border-line bg-panel p-2">
              <h2 className="px-3 pb-1 pt-3 text-base font-semibold text-ink">{t('dashboard.quickActions')}</h2>
              <QuickAction to="/cards/new" label={t('dashboard.createCard')} icon={CreditCard} />
              <QuickAction to="/links/new" label={t('dashboard.createLinkPage', 'Link-Seite erstellen')} icon={Link2} />
              <QuickAction to="/users" label={t('dashboard.inviteUsers')} icon={UserPlus} />
              <QuickAction to="/billing" label={t('dashboard.managePlan')} icon={Wallet} />
              <QuickAction to="/help" label={t('help.pageTitle')} icon={HelpCircle} />
              <QuickAction to="/settings" label={t('nav.settings')} icon={Settings} />
            </section>
          </aside>
        </div>
      </div>
    </Layout>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface Step {
  id: string;
  done: boolean;
  title: string;
  text: string;
  to: string;
  cta: string;
}

/** Card views of the last 7 days as bars (one per day). */
function ViewsPanel({ views }: { views: ViewStats }) {
  const { t } = useAppTranslation();
  const byDay = new Map(views.daily.map((d) => [d.day.slice(0, 10), d.views]));
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    const key = d.toISOString().slice(0, 10);
    return { key, label: d.toLocaleDateString('de-DE', { weekday: 'short' }), views: byDay.get(key) ?? 0 };
  });
  const max = Math.max(1, ...days.map((d) => d.views));
  return (
    <section className="rounded-2xl border border-line bg-panel p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-base font-semibold text-ink">{t('dashboard.viewsTitle', 'Aufrufe der letzten 7 Tage')}</h2>
        <Link to="/analytics" className="inline-flex items-center gap-1 text-sm text-accent-700 hover:text-accent-600 dark:text-accent-300 dark:hover:text-accent-200 transition-colors">
          {t('dashboard.viewsOpen', 'Analytics öffnen')} <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
      <p className="mt-3 text-3xl font-semibold tracking-tight text-ink tabular-nums">
        {views.total.toLocaleString('de-DE')}
        <span className="ml-2 text-sm font-normal text-ink-muted">
          {t('dashboard.viewsVisitors', 'von {{count}} Besuchern', { count: views.visitors })}
        </span>
      </p>
      {views.total === 0 ? (
        <p className="mt-3 text-sm text-ink-muted">{t('dashboard.viewsEmpty', 'Noch keine Aufrufe. Teile den QR-Code deiner Karte, dann siehst du sie hier.')}</p>
      ) : (
        <div className="mt-5 flex h-28 items-end gap-2" role="img" aria-label={days.map((d) => `${d.label}: ${d.views}`).join(', ')}>
          {days.map((d) => (
            <div key={d.key} className="flex h-full flex-1 flex-col items-center justify-end gap-1.5" title={`${d.label}: ${d.views}`}>
              <div
                className={`w-full rounded-t-md ${d.views > 0 ? 'bg-accent-500' : 'bg-raised'}`}
                style={{ height: `${Math.max(4, (d.views / max) * 100)}%` }}
              />
              <span className="text-[11px] text-ink-faint">{d.label}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function NumberRow({ to, label, value, of }: { to: string; label: string; value: string; of: string }) {
  return (
    <Link to={to} className="group flex items-center justify-between rounded-xl px-3 py-3 hover:bg-raised transition-colors">
      <span className="text-sm text-ink-muted group-hover:text-ink transition-colors">{label}</span>
      <span className="text-xl font-semibold tracking-tight text-ink tabular-nums">
        {value}
        {of && <span className="ml-1 text-sm font-normal text-ink-faint">{of}</span>}
      </span>
    </Link>
  );
}

function QuickAction({ to, label, icon: Icon }: { to: string; label: string; icon: LucideIcon }) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-ink-muted hover:bg-raised hover:text-ink transition-colors"
    >
      <Icon className="h-[18px] w-[18px] shrink-0 text-ink-faint group-hover:text-accent-400 transition-colors" strokeWidth={1.75} aria-hidden="true" />
      <span>{label}</span>
      <ChevronRight className="ml-auto h-4 w-4 text-ink-faint" aria-hidden="true" />
    </Link>
  );
}

function RoleBadge({ role }: { role: string }) {
  const roleLabel = useRoleLabel();
  const map: Record<string, string> = {
    owner:           'bg-accent-100 dark:bg-accent-500/20 text-accent-700 dark:text-accent-300',
    admin:           'bg-raised text-ink ring-1 ring-inset ring-line-strong',
    department_lead: 'bg-warning-100 dark:bg-warning-500/20 text-warning-700 dark:text-warning-300',
    member:          'bg-raised text-ink-muted',
    superadmin:      'bg-danger-100 dark:bg-danger-500/20 text-danger-700 dark:text-danger-300',
  };
  return (
    <span className={`flex-shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${map[role] ?? map['member']}`}>
      {roleLabel(role)}
    </span>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
}
